import {
  parseReviewDeepLink,
  reviewDeepLinkSearch,
  type ReviewDeepLinkState,
  type ReviewSection
} from "@workerstation/shared";
import { developmentIdentifier } from "@workerstation/shared";
import { stationPath } from "@workerstation/shared";

export interface ReviewEditorTab {
  readonly id: string;
  readonly title: string;
  readonly state: ReviewDeepLinkState;
}

export interface WorkspaceReviewTabProjection {
  readonly editorTabOrder: readonly string[];
  readonly activeEditorTab: string | null;
}

export const REVIEW_EDITOR_TAB_PREFIX = "review:" as const;

export function isReviewEditorTabId(value: unknown): value is string {
  if (value === `${REVIEW_EDITOR_TAB_PREFIX}overview`) {
    return true;
  }
  if (typeof value !== "string") return false;
  for (const [prefix, kind] of [
    [`${REVIEW_EDITOR_TAB_PREFIX}candidate:`, "candidate"],
    [`${REVIEW_EDITOR_TAB_PREFIX}change:`, "change"]
  ] as const) {
    if (!value.startsWith(prefix)) continue;
    try {
      developmentIdentifier(kind, value.slice(prefix.length));
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

/** Restore the prior editor surface when Back/Forward leaves review routing. */
export function editorTabAfterReviewExit(
  editorTabOrder: readonly string[],
  previousNonReviewTab: string | null,
  currentTab: string | null
): string | null {
  if (!Array.isArray(editorTabOrder)) {
    throw new TypeError("Editor tab order must be an array.");
  }
  if (!isReviewEditorTabId(currentTab)) return currentTab;
  if (
    previousNonReviewTab !== null &&
    !isReviewEditorTabId(previousNonReviewTab) &&
    editorTabOrder.includes(previousNonReviewTab)
  ) {
    return previousNonReviewTab;
  }
  return editorTabOrder.find((id) => !isReviewEditorTabId(id)) ?? null;
}

/** Create a snapshot-source-agnostic tab bound only to immutable review IDs. */
export function createReviewEditorTab(
  state: ReviewDeepLinkState
): ReviewEditorTab {
  const canonical = parseReviewDeepLink(reviewDeepLinkSearch(state));
  if (canonical.status !== "valid") {
    throw new TypeError("Review editor tab state is invalid.");
  }
  const subject = canonical.state.candidateId
    ? `candidate:${canonical.state.candidateId}`
    : canonical.state.changeId
      ? `change:${canonical.state.changeId}`
      : "overview";
  return Object.freeze({
    id: `${REVIEW_EDITOR_TAB_PREFIX}${subject}`,
    title: canonical.state.candidateId
      ? `Review · ${canonical.state.candidateId.slice(0, 12)}`
      : canonical.state.changeId
        ? `Review · ${canonical.state.changeId.slice(0, 12)}`
        : "Review",
    state: canonical.state
  });
}

/** Drop one-shot gate remediation intent when the reviewer changes sections. */
export function reviewStateForSection(
  state: ReviewDeepLinkState,
  section: ReviewSection
): ReviewDeepLinkState {
  const canonical = createReviewEditorTab(state).state;
  const {
    action: _action,
    conditionKey: _conditionKey,
    ...persistent
  } = canonical;
  return createReviewEditorTab({ ...persistent, section }).state;
}

/** Preserve Change review tabs while workspace-scoped editor surfaces rehydrate. */
export function projectReviewTabsAcrossWorkspace(
  workspaceFileTabIds: readonly string[],
  reviewTabs: readonly ReviewEditorTab[],
  previousActiveTab: string | null,
  fallbackFileTab: string | null
): WorkspaceReviewTabProjection {
  if (!Array.isArray(workspaceFileTabIds) || !Array.isArray(reviewTabs)) {
    throw new TypeError("Workspace review tab projection requires arrays.");
  }
  if (previousActiveTab !== null && typeof previousActiveTab !== "string") {
    throw new TypeError("Previous active editor tab must be a string or null.");
  }
  if (fallbackFileTab !== null && typeof fallbackFileTab !== "string") {
    throw new TypeError("Fallback editor tab must be a string or null.");
  }
  const fileIds = [...workspaceFileTabIds];
  for (const id of fileIds) {
    if (typeof id !== "string" || !id.startsWith("file:") || id.length === 5) {
      throw new TypeError("Workspace file tab identity is invalid.");
    }
    stationPath(id.slice(5));
  }
  const reviewIds = [...reviewTabs].map((tab) => {
    if (!tab || typeof tab !== "object") {
      throw new TypeError("Review tab must be an object.");
    }
    const canonical = createReviewEditorTab(tab.state);
    if (tab.id !== canonical.id || tab.title !== canonical.title) {
      throw new TypeError(
        "Review tab identity does not match its immutable state."
      );
    }
    return canonical.id;
  });
  const editorTabOrder = [...fileIds, ...reviewIds];
  if (new Set(editorTabOrder).size !== editorTabOrder.length) {
    throw new TypeError("Workspace review tab projection contains duplicate tabs.");
  }
  const activeEditorTab =
    previousActiveTab !== null && reviewIds.includes(previousActiveTab)
      ? previousActiveTab
      : fallbackFileTab !== null && fileIds.includes(fallbackFileTab)
        ? fallbackFileTab
        : (reviewIds[0] ?? null);
  return Object.freeze({
    editorTabOrder: Object.freeze(editorTabOrder),
    activeEditorTab
  });
}
